import {
  Box,
  Button,
  Container,
  HStack,
  Input,
  Link,
  Stack,
  Text,
} from '@chakra-ui/react'
import { ViewIcon, ViewOffIcon } from '@chakra-ui/icons'
import { getAuth, signInWithEmailAndPassword } from 'firebase/auth'
import { doc, getDoc, getFirestore } from 'firebase/firestore'
import { useState } from 'react'
import { Link as RouterLink, useNavigate } from 'react-router-dom'
import AuthHeader from '../../components/AuthHeader'
import useHandleError from '../../hooks/useHandleError'
import useSendPasswordResetEmail from '../../hooks/useSendPasswordResetEmail'
import FirestoreUser from '../../types/FirestoreUser'
import { primaryServices } from '../../utils/firebaseServiceContext'

export default () => {
  const [loading, setLoading] = useState(false)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [fieldType, setFieldType] = useState('password')
  const handleError = useHandleError()
  const navigate = useNavigate()
  const sendPasswordResetEmail = useSendPasswordResetEmail()

  const checkUserStatus = async (
    uid: string,
  ): Promise<{ isArchived: boolean; userData: FirestoreUser | undefined }> => {
    try {
      const userRef = doc(primaryServices.firestore, 'users', uid)
      const userSnap = await getDoc(userRef)
      const userData = userSnap.data() as FirestoreUser | undefined
      return {
        isArchived: userData?.archived === true,
        userData,
      }
    } catch (error) {
      console.error('Error checking user status:', error)
      return {
        isArchived: false,
        userData: undefined,
      }
    }
  }

  const handleSignIn = async () => {
    try {
      setLoading(true)
      const userCredential = await signInWithEmailAndPassword(
        primaryServices.auth,
        email,
        password,
      )

      // Check user status (archived and profile completion)
      const { isArchived, userData } = await checkUserStatus(
        userCredential.user.uid,
      )
      if (isArchived) {
        // Sign out the user immediately
        await primaryServices.auth.signOut()
        throw new Error(
          'Your account has been archived. Please contact support for assistance.',
        )
      }

      // Check if user has completed their profile
      if (userData?.profile?.agreedToProfileCheckbox === true) {
        navigate('/')
      } else {
        navigate('/profile')
      }
    } catch (error) {
      setLoading(false)
      handleError(error)
    }
  }

  const toggleFieldType = () => {
    if (fieldType === 'password') {
      setFieldType('text')
    } else {
      setFieldType('password')
    }
  }

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault()
        handleSignIn()
      }}
    >
      <Container>
        <Stack spacing={4}>
          <AuthHeader />
          <Stack spacing={2}>
            <Input
              placeholder='Email'
              type='email'
              autoComplete='email'
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <HStack>
              <Input
                placeholder='Password'
                type={fieldType}
                autoComplete='current-password'
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />

              <Button padding={5} onClick={toggleFieldType} variant='outline'>
                {fieldType === 'password' ? <ViewIcon /> : <ViewOffIcon />}
              </Button>
            </HStack>
          </Stack>
          <Button
            type='submit'
            width='full'
            isLoading={loading}
            onClick={handleSignIn}
          >
            Sign In
          </Button>

          <Stack spacing={1}>
            <Text fontSize='medium'>
              Don't have an account?{' '}
              <Link as={RouterLink} to='/sign-up' color='purple.600'>
                Sign Up
              </Link>
            </Text>
            <Box>
              <Button
                size='sm'
                variant='link'
                onClick={() => sendPasswordResetEmail(email)}
              >
                Forgot Your Password?
              </Button>
            </Box>
          </Stack>
        </Stack>
      </Container>
    </form>
  )
}
